import React, { useEffect, useState } from "react";
import "./styles.scss";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FormGenerator from "../../core/form-generator";
import {
  convertToCamelCaseFromUnderScore,
  formatDate,
  handleFormValidation,
} from "../../utils";
import moment from "moment";
import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { HOSTNAME, REST_URLS } from "../../utils/endpoints";
import { toast } from "react-toastify";
import Loader from "../../core/loader";
import dayjs from "dayjs";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { FY_MONTH, financialYears } from "./helper";
import DeleteIcon from "@mui/icons-material/Delete";

const COLORS = [
  "#d72638",
  "#3f88c5",
  "#1c7c54",
  "#6a0572",
  "#ff7700",
  "#007f5f",
  "#5f0f40",
  "#006d77",
  "#6a4c93",
  "#a4036f",
];

const EventCalendar = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const financialYearStart = currentMonth >= 3 ? currentYear : currentYear - 1;

  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState({
    courseStartDate: moment.utc().format("YYYY-MM-DDTHH:mm:ssZ"),
    courseEndDate: moment.utc().format("YYYY-MM-DDTHH:mm:ssZ"),
  });
  const [selectedYear, setSelectedYear] = useState(financialYearStart);
  const [calendarInitialDate, setCalendarInitialDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [errors, setErrors] = useState({});
  const [eventList, setEventList] = useState([]);
  const [UpdateBtn, setUpdateBtn] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    setCalendarInitialDate(`${year}-04-01`);
  };

  useEffect(() => {
    loadEvents({ financialYear: selectedYear });
  }, [selectedYear]);

  const normalizeEvents = (apiEvents = []) => {
    return apiEvents.map((event, index) => {
      const start = moment(event.courseDuration.startDate).format("YYYY-MM-DD");
      const end = moment(event.courseDuration.endDate).format("YYYY-MM-DD");

      return {
        id: event.id,
        title: event.name,
        start,
        end,
        backgroundColor: COLORS[index % COLORS.length],
        borderColor: COLORS[index % COLORS.length],
        extendedProps: { ...event },
      };
    });
  };

  const loadEvents = (params = {}) => {
    const financialYear = `${params?.financialYear}-${
      params?.financialYear + 1
    }`;
    const payload = {
      financialYear: financialYear,
    };
    setLoader(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${HOSTNAME}${REST_URLS.EVENT_SCHEDULE}`,
      null,
      payload
    )
      .then((res) => {
        if (!res.message) {
          setEventList(normalizeEvents(res));
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoader(false));
  };

  const getEventForm = () => [
    {
      label: "Event Name",
      name: "name",
      type: "text",
      required: true,
      value: formData.name || "",
    },
    {
      label: "Event Start Date",
      name: "startDate",
      type: "datePicker",
      required: true,
      value: formData.startDate || "",
      minDate: dayjs(),
    },
    {
      label: "Event End Date",
      name: "endDate",
      type: "datePicker",
      required: true,
      value: formData.endDate || "",
      minDate: dayjs(),
    },
  ];

  const submitCourseDuration = (isUpdate) => {
    const isValid = handleFormValidation(getEventForm(), setErrors, formData);
    const payload = {
      name: formData.name,
      courseDuration: {
        startDate: formatDate(formData.startDate, "YYYY-MM-DD"),
        endDate: formatDate(formData.endDate, "YYYY-MM-DD"),
      },
    };

    if (isValid) {
      const method = isUpdate ? HTTP_METHODS.PUT : HTTP_METHODS.POST;
      const url = isUpdate
        ? `${HOSTNAME}${REST_URLS.EVENT_SCHEDULE}/${formData.id}`
        : `${HOSTNAME}${REST_URLS.EVENT_SCHEDULE}`;

      invokeApi(method, url, payload)
        .then((response) => {
          if (response.code) {
            toast.error(response.message);
          } else {
            toast.success(
              `Event ${isUpdate ? "Updated" : "Created"} Successfully`
            );
            loadEvents({ financialYear: selectedYear });
            setUpdateBtn(false);
            resetForm();
          }
        })
        .catch(() => {
          toast.error(`Event ${isUpdate ? "update" : "creation"} failed`);
        });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      startDate: moment.utc().format("YYYY-MM-DDTHH:mm:ssZ"),
      endDate: moment.utc().format("YYYY-MM-DDTHH:mm:ssZ"),
    });
    setErrors({});
    setUpdateBtn(false);
    setSelectedEvent(null);
  };

  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event.extendedProps;
    setSelectedEvent(clickInfo.event);
    setFormData({
      id: event.id,
      name: event.name,
      startDate: new Date(event.courseDuration.startDate)
        ?.toISOString()
        .split("T")[0],
      endDate: new Date(event.courseDuration.endDate)
        .toISOString()
        .split("T")[0],
    });
    setUpdateBtn(true);
  };

  const handleDeleteEvent = () => {
    setLoader(true);
    invokeApi(
      HTTP_METHODS.DELETE,
      `${HOSTNAME}${REST_URLS.EVENT_SCHEDULE}/${selectedEvent.id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("Event deleted successfully");
          loadEvents({ financialYear: selectedYear });
          setSelectedEvent(null);
          resetForm();
        }
      })
      .catch(() => {
        toast.error("Failed to delete event");
      })
      .finally(() => setLoader(false));
  };

  const renderThreeMonthsListView = () => {
    navigate("/admin/training/event-month-enrollment-view", {
      state: { events: eventList },
    });
  };

  const renderEventContent = (eventInfo) => {
    return (
      <Tooltip title={eventInfo.event.title}>
        <div className="custom-event">{eventInfo.event.title}</div>
      </Tooltip>
    );
  };

  return (
    <>
      <div className="training-year-container">
        <div className="training-title-container">
          <div>
            <span className="training-title">Events</span>
            <div className="training-schedule">Full year schedule</div>
          </div>
          <div className="training-year">
            FY {selectedYear} - {Number(selectedYear) + 1}
          </div>
        </div>
        <div className="select-year">
          <FormControl sx={{ m: 1, minWidth: 120, display: "block" }}>
            <InputLabel id="financial-year-label">Select a Year</InputLabel>
            <Select
              labelId="financial-year-label"
              className="select-year-dropdown"
              value={selectedYear}
              onChange={handleYearChange}
              label="Select a Year"
            >
              {financialYears.map((year) => (
                <MenuItem key={year.value} value={year.value}>
                  {year.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <Tooltip title={`View Upcoming Events Sessions for 3 months`}>
          <Button
            className="month-view-btn"
            variant="contained"
            onClick={renderThreeMonthsListView}
          >
            Upcoming Events Sessions
          </Button>
        </Tooltip>
      </div>

      <div className="training-course">
        <div className="course-form">
          <div className="schedule-title">Schedule Event</div>
          <FormGenerator
            config={getEventForm()}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />
        </div>
        <div className="submit-btn">
          <Button
            variant="contained"
            onClick={() => submitCourseDuration(UpdateBtn)}
          >
            {UpdateBtn ? "Update" : "Submit"}
          </Button>
        </div>
        {selectedEvent && (
          <div className="submit-btn">
            <IconButton onClick={handleDeleteEvent}>
              <DeleteIcon color="error" />
            </IconButton>
          </div>
        )}
      </div>

      <div className="month-view-container" style={{ width: "auto" }}>
        <FullCalendar
          key={calendarInitialDate}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          initialDate={calendarInitialDate}
          events={eventList}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
        />
      </div>

      {loader && <Loader />}
    </>
  );
};

export default EventCalendar;
